import {
  EditOrderModal,
  GreenCheckIcon,
  Heading,
  InformationIcon,
  PencilIcon,
  PriceWithUnit,
  RedCheckIcon,
  TrashIcon,
} from '@/components';
import { User, Phone, MapPin, Package, Pencil, Trash2, CircleCheck, CircleX } from 'lucide-react';
import {
  COLUMN_CUSTOM_DETAILS,
  COLUMN_CUSTOMER_ADDRESS,
  COLUMN_IMPORT_VALID,
  COLUMN_ORDER_PAYMENT,
  COLUMN_PACKAGE_SIZE,
  COLUMN_PRODUCT_DETAILS,
  COLUMN_SHIPMENT,
  CUSTOM_ID,
  LOCAL_EN,
  ORDER_ACTIONS,
  ORDER_ID,
  STYLE_SHARE_TAG,
  THEME_VARIABLE,
} from '@/config';
import {
  ButtonFilter,
  ImportItem,
  OrderDataImport,
  OrderExcelResponse,
  PaymentType,
  ShipmentFeeType,
} from '@/types';
import { Button, Divider, Flex, message, Spin, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Key, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useBulkImportStore, useConfirmModalStore } from '@/stores';
import { ConfirmTypeEnum } from '@/components';
import {
  useDeleteOrderExcelById,
  useFetchOrderExcelByUID,
  useFetchOrderInValidExcelByUID,
  useFetchOrderValidExcelByUID,
  useRevalidateOrderExcel,
} from '@/hooksApi/useImport';
import { PackageSizeEnum, PackageSizeFormValues } from '@/components/OrderForm/PackageSizeCard';
import { CustomerFeeCell } from '@/components/ImportPreview/CustomerFeeCell';

const { Text } = Typography;

const userImportData = (
  id: string,
  current: any,
  buttonFilter: ButtonFilter,
  setCurrentPage: (page: number) => void,
  pageSize?: number,
  pickupAddressRegionUid?: string,
  selectedPickupAddressId?: string,
) => {
  const [editingOrderUid, setEditingOrderUid] = useState<string | undefined>(undefined);

  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const [idSelect, setIdSelect] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setPendingImport } = useBulkImportStore();
  const { openConfirm } = useConfirmModalStore();
  // Fetch All data
  const {
    data: dataOrderExcel,
    isLoading: loadingOrderExcel,
    isFetching: isFetchingOrderExcel,
  } = useFetchOrderExcelByUID(id, current, pageSize, buttonFilter === ButtonFilter.All);

  // Fetch valid data
  const {
    data: dataOrderValidExcel,
    isLoading: loadingOrderValidExcel,
    isFetching: isFetchingOrderValidExcel,
  } = useFetchOrderValidExcelByUID(id, current, pageSize, buttonFilter === ButtonFilter.VALID);

  // Fetch invalid data
  const {
    data: dataOrderInValidExcel,
    isLoading: loadingOrderInValidExcel,
    isFetching: isFetchingOrderInValidExcel,
  } = useFetchOrderInValidExcelByUID(id, current, pageSize, buttonFilter === ButtonFilter.INVALID);

  // Revalidate carrier matching
  const { mutate: revalidateExcel, isLoading: isRevalidating } = useRevalidateOrderExcel();

  const handleRevalidate = (onComplete?: () => void) => {
    revalidateExcel(
      { uid: id, savedPickUpAddressUid: selectedPickupAddressId },
      {
        onSuccess: () => {
          message.success(t('revalidation_complete'));
          queryClient.invalidateQueries(['import', id]);
          queryClient.invalidateQueries(['import-valid', id]);
          queryClient.invalidateQueries(['import-invalid', id]);
          onComplete?.();
        },
        onError: () => {
          message.error(t('revalidation_failed'));
          onComplete?.();
        },
      },
    );
  };

  // Delete order excel
  const { mutate: deleteOrderExcelById, isLoading: loadingDeleteOrder } = useDeleteOrderExcelById();

  const executeDelete = (key: string) => {
    deleteOrderExcelById(
      {
        key,
        uid: id,
      },
      {
        onSuccess: async () => {
          message.success(t('operation'));

          queryClient.invalidateQueries(['import', id]);
          queryClient.invalidateQueries(['import-valid', id]);
          queryClient.invalidateQueries(['import-invalid', id]);

          if (isTheOnlyItem()) {
            setCurrentPage(current - 1);
          }
        },
      },
    );
  };

  const handleDelete = (key: string) => {
    openConfirm({
      title: t('permission.delete_imported_order'),
      description: t('permission.delete_imported_order_description'),
      approveText: t('delete'),
      cancelText: t('cancel'),
      confirmType: ConfirmTypeEnum.DESTRUCTIVE,
      onConfirm: () => executeDelete(key),
      onCancel: () => setIdSelect(null),
    });
  };

  const isTheOnlyItem = () => {
    const tableItemList = getCurrentDataTable();

    return tableItemList.length === 1;
  };

  const getCurrentDataTable = () => {
    return buttonFilter === ButtonFilter.All
      ? mapOrderData(dataOrderExcel?.object?.items || [])
      : buttonFilter === ButtonFilter.VALID
        ? mapOrderData(dataOrderValidExcel?.object?.items || [])
        : mapOrderData(dataOrderInValidExcel?.object?.items || []);
  };
  const columns: ColumnsType<OrderDataImport> = [
    {
      title: t('validity'),
      dataIndex: COLUMN_IMPORT_VALID,
      key: COLUMN_IMPORT_VALID,
      render: (validity: string, record: OrderDataImport) => {
        const keysErrors = record?.errors && Object.keys(record?.errors);

        return validity === 'VALID' ? (
          <div className='flex justify-center'>
            <CircleCheck className='h-5 w-5 text-[hsl(145,55%,40%)]' strokeWidth={2} />
          </div>
        ) : (
          <Tooltip
            title={
              keysErrors &&
              keysErrors?.length > 0 && (
                <Flex vertical>
                  <Text>
                    {keysErrors?.map((key: string, index: number) => (
                      <span key={index}>
                        {key}
                        <br />
                      </span>
                    ))}
                  </Text>
                  <Divider />
                  {keysErrors?.map((error: string, index: Key | undefined) => (
                    <Text key={index}>{`. ${record?.errors[error]}`}</Text>
                  ))}
                </Flex>
              )
            }
            placement='right'
            color='#fff'
            overlayInnerStyle={{ color: THEME_VARIABLE.customTunaPrimary }}
          >
            <div className='flex justify-center'>
              <CircleX className='h-5 w-5 text-[#dc2828]' strokeWidth={2} />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: t('custom_id'),
      dataIndex: ORDER_ID,
      key: ORDER_ID,
      render: (text) => <Heading title={text} />,
    },
    {
      title: t('customer_details'),
      key: COLUMN_CUSTOM_DETAILS,
      render: (_, record: OrderDataImport) => (
        <Flex vertical gap={2}>
          <Flex align='center' gap={6}>
            <User size={14} className='text-gray-400 shrink-0' strokeWidth={1.5} />
            <span className='text-sm font-medium text-sd-foreground'>{record.customerName}</span>
          </Flex>
          <Flex align='center' gap={6}>
            <Phone size={12} className='text-[#737b8c] shrink-0' strokeWidth={1.5} />
            <span className='text-xs text-[#737b8c]'>{record.customerPhone}</span>
          </Flex>
        </Flex>
      ),
    },
    {
      title: t('customer_address'),
      dataIndex: COLUMN_CUSTOMER_ADDRESS,
      key: COLUMN_CUSTOMER_ADDRESS,
      render: (text) => (
        <Flex align='center' gap={6}>
          <MapPin size={14} className='text-red-500 shrink-0' strokeWidth={1.5} />
          <span className='text-sm text-gray-600'>{text}</span>
        </Flex>
      ),
    },
    {
      title: t('product_details'),
      key: COLUMN_PRODUCT_DETAILS,
      render: (record: OrderDataImport) => (
        <Flex align='center' gap={6}>
          <Package size={14} className='text-orange-500 shrink-0' strokeWidth={1.5} />
          <span className='text-sm text-sd-foreground whitespace-nowrap'>
            {record?.quantity ?? 0} {t('items')} ·{' '}
            {typeof record?.total === 'number' ? record.total.toLocaleString() : record?.total || 0}{' '}
            {t('iqd')}
          </span>
        </Flex>
      ),
    },
    {
      title: t('package_size'),
      dataIndex: COLUMN_PACKAGE_SIZE,
      key: COLUMN_PACKAGE_SIZE,
      render: (text) => (
        <span className='inline-flex items-center rounded-full bg-[#f1f0ee] px-2.5 py-0.5 text-xs font-medium text-sd-foreground capitalize'>
          {t(PackageSizeEnum[text as PackageSizeFormValues])}
        </span>
      ),
    },
    {
      title: t('order_payment'),
      dataIndex: COLUMN_ORDER_PAYMENT,
      key: COLUMN_ORDER_PAYMENT,
      render: (text) => {
        const label = text === PaymentType.COLLECT_ON_DELIVERY ? 'COD' : t('prepaid');
        return <span className='text-sm text-sd-foreground whitespace-nowrap'>{label}</span>;
      },
    },
    {
      title: t('shipment_payment'),
      dataIndex: COLUMN_SHIPMENT,
      key: COLUMN_SHIPMENT,
      render: (text) => {
        const label = text === ShipmentFeeType.BY_CUSTOMER ? t('customer') : t('merchant');
        return <span className='text-sm text-sd-foreground whitespace-nowrap'>{label}</span>;
      },
    },
    {
      // BOXY-414 — the sheet's fee_customer_payable, so a product price typed
      // into it is visible (and flagged) before the orders are created.
      title: t('customer_shipping_fee'),
      key: 'feeCustomerPayable',
      render: (record: OrderDataImport) => (
        <CustomerFeeCell
          fee={record.feeCustomerPayable}
          productsValue={record.total}
          shipmentFeeType={record.shipmentPayment}
          errors={record.errors}
        />
      ),
    },
    {
      title: t('actions'),
      key: ORDER_ACTIONS,
      fixed: isRtl ? ('left' as const) : ('right' as const),
      width: 88,
      render: (record: OrderDataImport) => (
        <Flex align='center' gap={8}>
          <Button
            onClick={() => setEditingOrderUid(record?.key)}
            className='import-action-btn-edit'
          >
            <Pencil size={16} strokeWidth={2} />
          </Button>
          <Button
            className='import-action-btn-delete'
            onClick={() => {
              handleDelete?.(record?.key);
              setIdSelect?.(record?.key);
            }}
          >
            {idSelect === record?.key ? (
              <Spin size='small' />
            ) : (
              <Trash2 size={14} strokeWidth={2} />
            )}
          </Button>
        </Flex>
      ),
    },
  ];

  const mapOrderData = (data: OrderExcelResponse['object']['items'] | undefined) => {
    return data && data.length > 0
      ? data.map((item: ImportItem) => {
          // Parse quantity: split by comma, sum values.
          // Also handle corrupted data where quantities were concatenated without commas
          // by comparing with title count.
          const titleCount =
            item.product_title?.split(',').filter((t: string) => t.trim()).length || 1;
          const rawQuantities =
            item.product_quantity
              ?.split(',')
              .map((q: string) => Number(q.trim()))
              .filter((n: number) => !isNaN(n) && n > 0) || [];
          const totalQuantity =
            rawQuantities.length > 0
              ? rawQuantities.reduce((acc: number, current: number) => acc + current, 0)
              : titleCount;

          return {
            key: item?.uid,
            validity: item?.validation_status || '',
            orderId: item?.custom_id || '',
            customerName: item?.full_name || '',
            customerPhone: item?.phone || '',
            customerAddress: item?.address_text || '',
            quantity: totalQuantity,
            total: item?.products_value || '',
            packageSize: item?.size || '',
            orderPayment: item?.payment_type || '',
            shipmentPayment: item?.shipment_fee_type || '',
            feeCustomerPayable: item?.fee_customer_payable ?? null,
            errors: (() => {
              if (!item?.errors) return {};
              if (typeof item.errors === 'object') return item.errors;
              try {
                return JSON.parse(item.errors);
              } catch {
                return {};
              }
            })(),
            carrierList: item?.carrierList ?? [],
            validation_status: item?.validation_status,
            recommended_carrier_slug: item?.recommended_carrier_slug,
            selected_carrier_slug: item?.selected_carrier_slug,
            available_carriers_detail: item?.available_carriers_detail,
            availableCarriersParsed: (() => {
              try {
                return JSON.parse(item?.available_carriers_detail || '[]');
              } catch {
                return [];
              }
            })(),
            has_carrier_error: item?.has_carrier_error,
            carrier_error_message: item?.carrier_error_message,
            // Item 2 — shipping-rule attribution for the recommended carrier.
            recommended_rule_name: item?.recommended_rule_name ?? null,
            recommended_rule_uid: item?.recommended_rule_uid ?? null,
          };
        })
      : [];
  };

  // Current view total (for pagination)
  const currentTotal =
    buttonFilter === ButtonFilter.All
      ? dataOrderExcel?.object?.total || 0
      : buttonFilter === ButtonFilter.VALID
        ? dataOrderValidExcel?.object?.total || 0
        : dataOrderInValidExcel?.object?.total || 0;

  // Overall counts from ALL data (cached from initial load, persists across tab switches)
  // React Query keeps stale data even when the query is disabled
  const allTotal = dataOrderExcel?.object?.total || 0;
  const allInvalidRowsCount = dataOrderExcel?.object?.invalid_rows_count || 0;

  return {
    columns,
    editingOrderUid: editingOrderUid,
    setEditingOrderUid: setEditingOrderUid,
    dataTables: getCurrentDataTable(),
    loadingOrderExcel:
      buttonFilter === ButtonFilter.All
        ? loadingOrderExcel || isFetchingOrderExcel
        : buttonFilter === ButtonFilter.VALID
          ? loadingOrderValidExcel || isFetchingOrderValidExcel
          : loadingOrderInValidExcel || isFetchingOrderInValidExcel,
    total: currentTotal,
    invalid_rows_count: allInvalidRowsCount,
    all_total: allTotal,
    all_invalid_rows_count: allInvalidRowsCount,
    handleRevalidate,
    isRevalidating,
  };
};

export default userImportData;
